import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../assets/scss/common.scss';
import '../../assets/reset.css';
import { logger } from '@/utils/logger.ts';
import { sendEvent } from '@/api/nativeBridge.ts';
import { useTheme, useUserStore } from '@/hooks/useStore.ts';
import { useDiscoverQuery } from '@/queries/hooks/useApiQuery';
import { getAbType } from '@/api/abTest.ts';

export interface GuideList {
  GUIDE: GuideData[];
}
export interface GuideData {
  title: string;
  content: string;
  imageUrl: string;
  appLink: string;
}

const LaboratoryPage: React.FC = () => {
  const [guides, setGuides] = useState<GuideData[]>([]);
  const [currentHours, setCurrentHours] = useState(new Date().getHours());
  const theme = useTheme();
  const isAgreed = useUserStore((state) => state.isAgreed);
  const isAdult = useUserStore((state) => state.isAdult);
  const navigate = useNavigate();
  const [abType, setAbType] = useState<'A' | 'B' | undefined>(undefined);
  const { data, isLoading, isError, error, refetch } = useDiscoverQuery();

  useEffect(() => {
    (async () => {
      const abType = await getAbType();
      setAbType(abType);
    })();
  }, []);

  useEffect(() => {
    if (!data) return;

    logger.debug(data);
    if (data.GUIDE?.length > 0) {
      setGuides(data.GUIDE);
    }
  }, [data]);

  if (isError) {
    logger.error('Error fetching data:', error);
  }

  // 네이티브 호출 로직
  const handleButtonClick = (appLink: string, tittle: string) => {
    logger.debug(appLink);
    logger.debug(tittle);
    // sendEvent('SEND_LOG', {
    //     type: 'GA4',
    //     key: '컨텐츠영역',
    //     message: tittle,
    // });

    if (appLink.startsWith('/route?url=')) {
      const url = new URLSearchParams(appLink.split('?')[1]).get('url');
      if (url) navigate(url);
    } else if (
      window.webkit &&
      window.webkit.messageHandlers &&
      window.webkit.messageHandlers.dataHandler
    ) {
      window.webkit.messageHandlers.dataHandler.postMessage(appLink);
    } else if ((window as any).Android) {
      (window as any).Android.dataHandler(appLink);
    } else {
      logger.error('Native interface not available.');
    }
  };

  // background 에서 돌아올때 시간에 맞춰서 시간 업데이트
  useEffect(() => {
    sendEvent('GET_CONFIG');
    sendEvent('GET_TOKEN');

    const handleRefreshView = () => {
      setCurrentHours(new Date().getHours());
    };

    window.addEventListener('REFRESH_VIEW_CALLBACK', handleRefreshView);
    return () => {
      window.removeEventListener('REFRESH_VIEW_CALLBACK', handleRefreshView);
    };
  }, []);

  const today = useMemo(() => new Date(), []);
  const month = today.getMonth() + 1;
  const date = today.getDate();
  const day = useMemo(() => {
    const weekDay = [
      '일요일',
      '월요일',
      '화요일',
      '수요일',
      '목요일',
      '금요일',
      '토요일',
    ];
    return weekDay[today.getDay()];
  }, [today]);

  const timeOfDayClass = useMemo(() => {
    if (currentHours >= 6 && currentHours < 11) {
      return 'morning';
    } else if (currentHours >= 11 && currentHours < 17) {
      return 'afternoon';
    } else {
      return 'evening';
    }
  }, [currentHours]);

  if (isAgreed !== undefined && isAgreed === true) {
    navigate('/search');
  }

  // TODO : remove this
  const onTestPageClick = () => {
    const mode = import.meta.env.MODE; // Vite에서 mode 확인
    if (mode == 'development') {
      navigate('/native-test');
    }
  };

  return (
    <div className={`ixi-O-container pt0 ${timeOfDayClass} ${theme}`}>
      <div className="ixi-O-contents-view">
        {/* 원본 HTML 기준으로 지구(?)가 보이는 배경 영역 */}
        <div className={`ixi-O-contents-view__bg-earth ${timeOfDayClass}`}>
          {/*<img src='/res/img/evening.gif' alt='Time of day image' />*/}
          {/* 날짜 */}
          <p className="date">
            {month}월 {date}일 {day}
          </p>
        </div>

        {/* 상단 헤더(비어있는 div) */}
        <div className="ixi-O-contents-view__header"></div>

        {/* 메인 콘텐츠 영역 */}
        <div className="ixi-O-contents-view__contents type-guide">
          {isAdult === true && (
            <>
              {/* 상단 실험실 기능 안내 타이틀/설명/이미지/버튼 */}
              <div className="title-wrap">
                <p className="title" onClick={onTestPageClick}>
                  AI가 대신 검색해주는
                  <br />
                  실험실 기능을 사용해 보세요.
                </p>
                <p className="desc">
                  장소 관련 정보를 쇼츠로 검색해서 제공해줘요.
                </p>
              </div>
              <div className="img-wrap">
                {abType === 'A' && (
                  <img
                    src="./res/img/discover_tutorial_1_ver_15fps.gif"
                    alt="Tutorial A"
                  />
                )}
                {abType === 'B' && (
                  <img
                    src="./res/img/discover_tutorial_B_ver2_3.gif"
                    alt="Tutorial B"
                  />
                )}
              </div>

              <div className="btn-wrap">
                {/* 사용하고 추천받기 버튼 클릭 시 어떤 로직을 적용할지 지정 */}
                <button
                  type="button"
                  onClick={() => sendEvent('GO_DISCOVER_SETTING')}
                >
                  사용하고 제공받기
                </button>
              </div>
            </>
          )}

          {/* 익시오 활용 가이드 목록 */}
          <div className="ixi-O-guide-wrap">
            <p className="title">익시오 활용 가이드</p>
            <ul className="guide-list">
              {guides.map((guide, index) => (
                <li
                  className="item"
                  key={index}
                  onClick={() => handleButtonClick(guide.appLink, guide.title)}
                >
                  <img
                    className="item__img"
                    src={guide.imageUrl}
                    alt="가이드 이미지"
                  />
                  <div className="item__title">
                    <p className="title">{guide.title}</p>
                    <p className="desc">{guide.content}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          {/* // 익시오 활용 가이드 목록 끝 */}
        </div>
      </div>
    </div>
  );
};

export default LaboratoryPage;
